import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/services/LoggingService";
import { useAuthStore } from "@/stores/authStore";
import { getSignupEnabled, upsertIntakeResponses, updateUserProfileOnboarding, ensureWaitlistByEmail, updateWaitlistOnboarding, type IntakeAnswers } from "@/services/onboarding/intakeService";
import { getDefaultCurrencyForCountry } from "@/constants/countries";

const LS_KEY = "alts__welcome_intro_v1";

function parseFullName(input?: string): { first_name: string | null; last_name: string | null; full_name: string | null } {
  const s = (input || '').trim();
  if (!s) return { first_name: null, last_name: null, full_name: null };
  const parts = s.split(/\s+/);
  if (parts.length === 1) return { first_name: parts[0], last_name: null, full_name: s };
  return { first_name: parts[0], last_name: parts.slice(1).join(' '), full_name: s };
}

export interface WelcomeGateState {
  shouldShow: boolean;
  forceOpen: boolean;
  isLoading: boolean;
  submit: (answers: IntakeAnswers & { full_name?: string; professional_designations?: string[] }) => Promise<"waitlist" | "dashboard">;
  dismiss: () => Promise<"waitlist" | "dashboard">;
  userEmail?: string;
}

export function useWelcomeIntroGate(): WelcomeGateState {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);
  const [shouldShow, setShouldShow] = useState(false);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);

  const forceOpen = useMemo(() => {
    if (typeof window === "undefined") return false;
    const url = new URL(window.location.href);
    return url.searchParams.get("welcome") === "1";
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      try {
        setIsLoading(true);
        const email = user?.email || undefined;
        setUserEmail(email);

        if (!user?.id && !email) {
          setShouldShow(false);
          return;
        }

        if (typeof window !== "undefined") {
          try {
            const v = window.localStorage.getItem(LS_KEY);
            if (v) {
              // If the key exists at all, treat as "already shown" locally.
              // Keep forceOpen override to allow explicit testing via ?welcome=1
              setShouldShow(forceOpen);
              return;
            }
          } catch {}
        }

        if (user?.id) {
          const { data: profile, error } = await supabase
            .from("user_profiles")
            .select("persona,welcome_seen_at")
            .eq("id", user.id)
            .single();
          if (!error && profile) {
            const show = !profile.persona || !profile.welcome_seen_at;
            setShouldShow(forceOpen || show);
            return;
          }
        }

        if (email) {
          const { data: wl, error: wle } = await supabase
            .from("user_waitlist")
            .select("persona,welcome_seen_at")
            .eq("email", email)
            .maybeSingle();
          if (!wle && wl) {
            const show = !wl.persona || !wl.welcome_seen_at;
            setShouldShow(forceOpen || show);
            return;
          }
        }

        setShouldShow(forceOpen);
      } catch (e: any) {
        logger.warn("useWelcomeIntroGate", "failed to decide", { error: String(e?.message || e) });
        setShouldShow(forceOpen);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [user?.id, user?.email, forceOpen]);

  const writeLocalDismiss = () => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(LS_KEY, JSON.stringify({ status: "dismissed", ts: Date.now() }));
    } catch {}
  };

  const routeOutcome = async (): Promise<"waitlist" | "dashboard"> => {
    // If the user is authenticated, always route to dashboard.
    if (user?.id) return "dashboard";
    const enabled = await getSignupEnabled();
    return enabled ? "dashboard" : "waitlist";
  };

  const dismiss = async () => {
    try {
      if (user?.id) {
        await updateUserProfileOnboarding(user.id, { welcome_seen_at: new Date().toISOString() });
      } else if (userEmail) {
        await updateWaitlistOnboarding(userEmail, { welcome_seen_at: new Date().toISOString() });
      }
    } catch (e: any) {
      logger.warn("useWelcomeIntroGate.dismiss", "db write failed, local fallback", { error: String(e?.message || e) });
    }
    writeLocalDismiss();
    return await routeOutcome();
  };

  const submit: WelcomeGateState["submit"] = async (answers) => {
    try {
      if (!userEmail && !user?.id) return "dashboard";
      const parsed = parseFullName(answers.name);

      if (user?.id) {
        const { data: existingProfile } = await supabase
          .from("user_profiles")
          .select("first_name,last_name,country,citizenship,default_currency,professional_designations,persona")
          .eq("id", user.id)
          .maybeSingle();

        const parsedFirst = (parsed.first_name || '').trim();
        const parsedLast = (parsed.last_name || '').trim();
        const firstNameUpdate = (!existingProfile?.first_name && parsedFirst) ? parsedFirst : undefined;
        const lastNameUpdate = (!existingProfile?.last_name && parsedLast) ? parsedLast : undefined;
        const countryUpdate = answers.country || undefined;
        const citizenshipUpdate = answers.citizenship || undefined;
        const normalizedCurrency = (answers.default_currency ?? '').toString().trim().toUpperCase();
        const existingCode = (existingProfile?.default_currency || '').toString().trim().toUpperCase();
        const countryFallback = answers.country ? getDefaultCurrencyForCountry(answers.country) : undefined;
        const hasChange = !!normalizedCurrency && normalizedCurrency !== existingCode;
        const defaultCurrencyUpdate = hasChange ? normalizedCurrency : (!existingCode ? (normalizedCurrency || countryFallback) : undefined);
        const personaUpdate = answers.persona || undefined;
        const designationsUpdate = (answers.advisor_credentials !== undefined) ? answers.advisor_credentials : undefined;

        logger.info("Welcome.submit", "profile.update.start", {
          userId: user.id,
          persona: personaUpdate,
          country: countryUpdate,
          citizenship: citizenshipUpdate,
          default_currency: defaultCurrencyUpdate,
          preserve_existing_currency: !!existingCode && !hasChange,
        });

        await updateUserProfileOnboarding(user.id, {
          persona: personaUpdate,
          professional_designations: designationsUpdate,
          country: countryUpdate,
          citizenship: citizenshipUpdate,
          default_currency: defaultCurrencyUpdate,
          linkedin_profile: answers.linkedin_profile,
          first_name: firstNameUpdate,
          last_name: lastNameUpdate,
          welcome_seen_at: new Date().toISOString(),
        });

        // Confirm persisted value and refresh profile cache
        try {
          const { data: confirm } = await supabase
            .from("user_profiles")
            .select("default_currency")
            .eq("id", user.id)
            .maybeSingle();
          logger.info("Welcome.submit", "profile.update.confirm", {
            userId: user.id,
            default_currency: (confirm as any)?.default_currency ?? null,
          });
        } catch {}
        await queryClient.invalidateQueries({ queryKey: ["profile-data", user.id] });

        logger.info("Welcome.submit", "profile.update.done", {
          userId: user.id,
          persona: personaUpdate,
          default_currency: defaultCurrencyUpdate,
        });
      } else if (userEmail) {
        await ensureWaitlistByEmail(userEmail);

        const { data: existingWaitlist } = await supabase
          .from("user_waitlist")
          .select("full_name,country,default_currency,professional_designations,persona")
          .eq("email", userEmail)
          .maybeSingle();

        const fullNameUpdate = (!existingWaitlist?.full_name && parsed.full_name) ? parsed.full_name : undefined;
        const wlCountryUpdate = answers.country || undefined;
        const wlNormalizedCurrency = (answers.default_currency ?? '').toString().trim().toUpperCase();
        const wlExistingCode = (existingWaitlist?.default_currency || '').toString().trim().toUpperCase();
        const wlCountryFallback = answers.country ? getDefaultCurrencyForCountry(answers.country) : undefined;
        const wlHasChange = !!wlNormalizedCurrency && wlNormalizedCurrency !== wlExistingCode;
        const wlDefaultCurrencyUpdate = wlHasChange ? wlNormalizedCurrency : (!wlExistingCode ? (wlNormalizedCurrency || wlCountryFallback) : undefined);
        const wlPersonaUpdate = answers.persona || undefined;
        const wlDesignationsUpdate = (answers.advisor_credentials !== undefined) ? answers.advisor_credentials : undefined;

        logger.info("Welcome.submit", "waitlist.update.start", {
          email: userEmail,
          persona: wlPersonaUpdate,
          country: wlCountryUpdate,
          default_currency: wlDefaultCurrencyUpdate,
          preserve_existing_currency: !!wlExistingCode && !wlHasChange,
        });

        await updateWaitlistOnboarding(userEmail, {
          persona: wlPersonaUpdate,
          professional_designations: wlDesignationsUpdate,
          country: wlCountryUpdate,
          citizenship: answers.citizenship,
          default_currency: wlDefaultCurrencyUpdate,
          onboarding_responses: { ...answers, name: answers.name, parsed },
          full_name: fullNameUpdate,
          welcome_seen_at: new Date().toISOString(),
        });

        // Confirm persisted value for waitlist (optional) and move on
        try {
          const { data: wlConfirm } = await supabase
            .from("user_waitlist")
            .select("default_currency")
            .eq("email", userEmail)
            .maybeSingle();
          logger.info("Welcome.submit", "waitlist.update.confirm", {
            email: userEmail,
            default_currency: (wlConfirm as any)?.default_currency ?? null,
          });
        } catch {}

        logger.info("Welcome.submit", "waitlist.update.done", {
          email: userEmail,
          persona: wlPersonaUpdate,
          default_currency: wlDefaultCurrencyUpdate,
        });
      }

      if (userEmail) {
        await upsertIntakeResponses({ email: userEmail, user_id: user?.id, answers });
      }
    } catch (e: any) {
      logger.warn("useWelcomeIntroGate.submit", "failed", { error: String(e?.message || e) });
    }
    writeLocalDismiss();
    return await routeOutcome();
  };

  return { shouldShow, forceOpen, isLoading, submit, dismiss, userEmail };
}
